<template>
  <div :class="arg.type == 'group' ? 'expression-template-input-group' : 'expression-template-input-line'">
    <template v-if="arg.type == 'group'">
      <div class="expression-template-input-group__header">
        <span class="label">
          <template v-if="arg.value == 'all_targets'">{{ $t("labels.allTargets") }}</template>
          <template v-if="arg.value == 'all_resources'">{{ $t("labels.allResources") }}</template>
          <template v-if="arg.value == 'all_indices'">{{ $t("labels.allIndices") }}</template>
        </span>
        <button type="button" class="button-icon button-item__icon" @click="emit('remove')">
          <lucide-icon name="x" />
        </button>
      </div>
      <div class="expression-template-input-group__body">
        <expression-template-input-line
          v-for="(arg, index) of groupArgs"
          :key="index"
          :arg="arg"
          :factored="factored"
          :expression-parse-context="expressionParseContext"
          class="-indented"
          @remove="replaceGroup(index)"
          @update:arg="updateGroupArg(index, $event)"
        />
      </div>
    </template>
    <template v-else>
      <button-item :icons="['x']" @click:x="emit('remove')" class="expression-template-input-line__item">
        <span v-if="templateArgName">{{ templateArgName }}</span>
        <span v-else class="placeholder-text">{{ $t("labels.unnamed") }}</span>
      </button-item>
  
      <div v-if="factored" class="line">
        <span>*</span>
        <input-number
          :value="arg.factor ?? 1"
          step="any"
          style="width: 4rem"
          @update:value="updateArgFactor($event)"
        />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { resolveGroups } from "./expressionTemplates";

import ButtonItem from "@core/input/ButtonItem.vue";
import LucideIcon from "@core/ui/LucideIcon.vue";
import InputNumber from "@core/input/InputNumber.vue";

import { useExpressionMenu } from "./useExpressionMenu";
import type { TemplateArg, TemplateSingleArg } from "./expressionTemplates";
import type { ExpressionParseContext } from "../effect/Expression";

const props = defineProps<{
  arg: TemplateArg;
  factored: boolean;
  expressionParseContext: ExpressionParseContext
}>();
const emit = defineEmits<{
  (e: "remove"): void;
  (e: "replace", args: TemplateSingleArg[]): void;
  (e: "update:arg", arg: TemplateArg): void;
}>();

const {
  elements,
  variables,
} = useExpressionMenu();

const groupArgs = computed(() => {
  if (props.arg.type == "group") {
    return resolveGroups([props.arg], props.expressionParseContext);
  }
  return [];
});

function replaceGroup(indexToRemove: number) {
  const newArgs = groupArgs.value.slice(0, indexToRemove).concat(groupArgs.value.slice(indexToRemove + 1));
  emit("replace", newArgs);
}

function updateArgFactor(value: number) {
  if (props.arg.type === "group") {
    return;
  }
  const updatedArg = { ...props.arg, factor: value };
  emit("update:arg", updatedArg);
}

function updateGroupArg(index: number, arg: TemplateArg) {
  if (props.arg.type !== "group") {
    return;
  }
  if (arg.type == "group") {
    return;
  }
  const factors = { ...props.arg.factors };
  factors[arg.value] = arg.factor ?? 1;
  const updatedArg = { ...props.arg, factors };
  emit("update:arg", updatedArg);
}

const templateArgName = computed(() => getTemplateArgName(props.arg));

function getTemplateArgName(arg: TemplateArg) {
  if (arg.type == "variable") {
    const variable = variables.value.find((v) => v.code == arg.value);
    if (!variable) {
      return arg.value;
    }
    return variable.title || "";
  } else if (arg.type == "item") {
    const item = elements.value.find((i) => i.uid == arg.value);
    if (!item) {
      return arg.value;
    }
    return item.title || "";
  } else {
    return arg.value;
  }
}
</script>

<style lang="scss">
.expression-template-input-group {
  width: 26rem;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  &__header {
    width: 20rem;
    max-width: 100%;
    display: flex;
    justify-content: space-between;
    gap: 0.5rem;
    align-items: center;
    padding-left: 0.5rem;
  }
  &__body {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding-left: 0.5rem;
  }
}
.expression-template-input-line {
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &__item {
    width: 20rem;
    min-width: 0;
    max-width: 100%;
  }

  &.-indented &__item {
    width: 19.5rem;
  }
}
</style>